import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';

import { Badge } from '../../components/ui/Badge.jsx';
import { Button } from '../../components/ui/Button.jsx';
import { MetricCard } from '../../components/ui/Card.jsx';
import { Table } from '../../components/ui/Table.jsx';
import { useTranslation } from '../../i18n/useTranslation.js';
import { hrApi } from '../../services/hrApi.js';

import { ReviewCreateModal } from './ReviewCreateModal.jsx';
import { ReviewDetailModal } from './ReviewDetailModal.jsx';

const STATUS_VARIANT = { DRAFT: 'neutral', SUBMITTED: 'info', ACKNOWLEDGED: 'success' };

function ratingStars(rating) {
  if (!rating) return '-';
  return '★'.repeat(rating) + '☆'.repeat(5 - rating);
}

export function ReviewsPage() {
  const { t } = useTranslation();
  const [createOpen, setCreateOpen] = useState(false);
  const [detailId, setDetailId] = useState(null);

  const list = useQuery({ queryKey: ['hr', 'reviews'], queryFn: () => hrApi.listReviews().then((r) => r.items) });
  const rows = list.data || [];
  const count = (s) => rows.filter((r) => r.status === s).length;

  const columns = [
    { key: 'reviewNumber', label: t('rev.col.number'), render: (r) => <span className="mono">{r.reviewNumber}</span> },
    { key: 'employee', label: t('rev.col.employee'), render: (r) => r.employeeName },
    { key: 'period', label: t('rev.col.period') },
    { key: 'rating', label: t('rev.col.rating'), render: (r) => <span className="mono" title={r.overallRating ? `${r.overallRating}/5` : ''}>{ratingStars(r.overallRating)}</span> },
    { key: 'status', label: t('col.status'), render: (r) => <Badge variant={STATUS_VARIANT[r.status]}>{t(`rev.status.${r.status}`)}</Badge> },
    { key: 'actions', label: '', align: 'right', render: (r) => <Button size="sm" variant="ghost" onClick={() => setDetailId(r.id)}>{t('rev.action.view')}</Button> },
  ];

  return (
    <section className="page">
      <header className="page-header">
        <div>
          <h1>{t('rev.title')}</h1>
          <p className="muted">{t('rev.subtitle')}</p>
        </div>
        <div className="page-actions">
          <Button variant="primary" onClick={() => setCreateOpen(true)}>{t('rev.action.new')}</Button>
        </div>
      </header>

      <div className="card-grid">
        <MetricCard label={t('rev.metric.total')} value={String(rows.length)} />
        <MetricCard label={t('rev.metric.draft')} value={String(count('DRAFT'))} />
        <MetricCard label={t('rev.metric.acknowledged')} value={String(count('ACKNOWLEDGED'))} trendVariant="success" />
      </div>

      <Table columns={columns} rows={rows} isLoading={list.isLoading} emptyMessage={t('rev.empty')} />

      <ReviewCreateModal open={createOpen} onClose={() => setCreateOpen(false)} />
      <ReviewDetailModal reviewId={detailId} onClose={() => setDetailId(null)} />
    </section>
  );
}
